import { parseCsv } from "./csv"
import type { BulkCreateTeamRow } from "./api/cccdAdminClient"

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const NATIONAL_ONLY_HEADERS = ["Categoría", "Plan de pago"]
const COMMON_HEADERS = ["Nombre del equipo", "Institución educativa", "Correo del capitán", "Nombre del capitán", "Becado (si/no)"]

/** Plantilla esperada del CSV. `hasCategoryPlan` es false para eventos regionales (categoría/plan fijos, sin selección). */
export function teamCsvTemplateHeaders(hasCategoryPlan: boolean): string[] {
  return hasCategoryPlan
    ? ["Nombre del equipo", ...NATIONAL_ONLY_HEADERS, ...COMMON_HEADERS.slice(1)]
    : COMMON_HEADERS
}

type FieldKey = "name" | "categoryId" | "planId" | "institutionName" | "captainEmail" | "captainName" | "becado"

function normalize(text: string): string {
  return text
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .trim()
    .toLowerCase()
}

const FIELD_MATCHERS: { key: FieldKey; test: (h: string) => boolean }[] = [
  { key: "name", test: (h) => h.includes("nombre") && h.includes("equipo") },
  { key: "categoryId", test: (h) => h.includes("categoria") },
  { key: "planId", test: (h) => h.includes("plan") },
  { key: "institutionName", test: (h) => h.includes("institucion") },
  { key: "captainEmail", test: (h) => h.includes("correo") },
  { key: "captainName", test: (h) => h.includes("capitan") && h.includes("nombre") },
  { key: "becado", test: (h) => h.includes("becado") },
]

function detectColumns(header: string[]): Partial<Record<FieldKey, number>> {
  const columns: Partial<Record<FieldKey, number>> = {}
  const usedKeys = new Set<FieldKey>()
  header.forEach((headerCell, index) => {
    const normalized = normalize(headerCell)
    const matcher = FIELD_MATCHERS.find((m) => !usedKeys.has(m.key) && m.test(normalized))
    if (matcher) {
      columns[matcher.key] = index
      usedKeys.add(matcher.key)
    }
  })
  return columns
}

function cell(cells: string[], columns: Partial<Record<FieldKey, number>>, key: FieldKey): string {
  const index = columns[key]
  return index === undefined ? "" : (cells[index] ?? "").trim()
}

function resolveBoolean(rawValue: string): boolean {
  return ["si", "s", "yes", "true", "x", "1"].includes(normalize(rawValue))
}

// Duplicados dentro del propio archivo por nombre normalizado — mismo
// criterio que la unicidad por evento aplicada en el backend (ver
// design.md, Decisión 1). El backend re-valida contra equipos ya
// existentes en el evento; esto solo detecta duplicados intra-archivo.
function dedupeKey(name: string): string {
  return name.replace(/\s+/g, " ").trim().toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "")
}

export type ParsedTeamRow = {
  line: number
  name: string
  error?: string
  warnings: string[]
  row: BulkCreateTeamRow
}

export function parseTeamsCsv(text: string, opts: { hasCategoryPlan: boolean }): { rows: ParsedTeamRow[]; error?: string } {
  const table = parseCsv(text)
  if (table.length === 0) {
    return { rows: [], error: "El archivo está vacío." }
  }

  const columns = detectColumns(table[0])
  const missingRequired =
    columns.name === undefined || (opts.hasCategoryPlan && (columns.categoryId === undefined || columns.planId === undefined))
  if (missingRequired) {
    return {
      rows: [],
      error: opts.hasCategoryPlan
        ? 'No se encontraron las columnas "Nombre del equipo", "Categoría" y "Plan de pago" en la primera fila del CSV. Verifica que uses la plantilla esperada.'
        : 'No se encontró la columna "Nombre del equipo" en la primera fila del CSV. Verifica que uses la plantilla esperada.',
    }
  }

  const seenNames = new Set<string>()

  const rows: ParsedTeamRow[] = table.slice(1).map((cells, i) => {
    const line = i + 2
    const name = cell(cells, columns, "name")
    const warnings: string[] = []

    if (!name) {
      return { line, name, error: "Falta el nombre del equipo", warnings, row: null as never }
    }

    const key = dedupeKey(name)
    if (seenNames.has(key)) {
      return { line, name, error: "Nombre duplicado dentro del mismo archivo", warnings, row: null as never }
    }
    seenNames.add(key)

    if (opts.hasCategoryPlan && !cell(cells, columns, "categoryId")) {
      return { line, name, error: "Falta la categoría", warnings, row: null as never }
    }
    if (opts.hasCategoryPlan && !cell(cells, columns, "planId")) {
      return { line, name, error: "Falta el plan de pago", warnings, row: null as never }
    }

    const captainEmail = cell(cells, columns, "captainEmail")
    const captainName = cell(cells, columns, "captainName")
    if (captainEmail && !EMAIL_RE.test(captainEmail)) {
      return { line, name, error: "Correo del capitán inválido", warnings, row: null as never }
    }
    if (Boolean(captainEmail) !== Boolean(captainName)) {
      warnings.push("Correo y nombre del capitán deben venir juntos — se omitirá el responsable")
    }

    const institutionName = cell(cells, columns, "institutionName")
    if (!institutionName) warnings.push("Sin institución educativa — se creará el equipo sin institución asociada")

    const becado = resolveBoolean(cell(cells, columns, "becado"))
    if (becado) warnings.push("Marcado como becado — se creará activo, sin fila de pago")

    const row: BulkCreateTeamRow = {
      line,
      name,
      categoryId: opts.hasCategoryPlan ? cell(cells, columns, "categoryId") : undefined,
      planId: opts.hasCategoryPlan ? cell(cells, columns, "planId") : undefined,
      institutionName: institutionName || undefined,
      captainEmail: captainEmail && captainName ? captainEmail : undefined,
      captainName: captainEmail && captainName ? captainName : undefined,
      discountType: becado ? "scholarship" : undefined,
      discountPercent: becado ? 100 : undefined,
      initialStatus: becado ? "active" : undefined,
    }

    return { line, name, warnings, row }
  })

  return { rows }
}
